import { useState, useEffect, useRef, useCallback } from 'react'
import type { ReactNode } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { VideoService, type VideoWithCategory } from '../services/videos'
import { ChatPromptService, type ChatPromptWithVideo } from '../services/chatPrompts'
import { AuthService } from '../services/auth'
import { CategoryService } from '../services/categories'
import { AttachmentService, type VideoAttachment } from '../services/attachments'
import ChatInterface from '../components/ChatInterface'
import VideoThumbnail from '../components/VideoThumbnail'
import type { VideoConversationContext, ChatInterfaceRef } from '../types/chat'
import iconVideo from '../assets/icons/icon-video.png'
import iconChat from '../assets/icons/icon-chat.png'
import hashtagIcon from '../assets/icons/hashtag-solid.png'

// DownloadSection component for attachment downloads
interface DownloadSectionProps {
  attachments: VideoAttachment[]
  label: ReactNode
  className: string
  downloadUrls: Record<string, string>
}

const DownloadSection = ({
  attachments,
  label,
  className,
  downloadUrls
}: DownloadSectionProps) => {
  const getUrlForAttachment = (attachmentId: string) => {
    return downloadUrls[attachmentId]
  }

  const isUrlReady = (attachmentId: string) => {
    return Boolean(getUrlForAttachment(attachmentId))
  }

  return (
    <div className="relative">
      {/* Main download link */}
      {isUrlReady(attachments[0].id) ? (
        <a
          href={getUrlForAttachment(attachments[0].id)}
          target="_blank"
          rel="noopener noreferrer"
          className={`${className} px-6 py-3 rounded-lg text-sm font-medium leading-snug text-center transition-colors flex items-center justify-center h-full no-underline`}
        >
          {label}
        </a>
      ) : (
        <button
          disabled={true}
          className={`${className} disabled:opacity-50 px-6 py-3 rounded-lg text-sm font-medium leading-snug text-center transition-colors flex items-center justify-center h-full`}
        >
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
          読み込み中...
        </button>
      )}

      {/* Additional attachments dropdown */}
      {attachments.length > 1 && (
        <div className="absolute top-full left-0 mt-1 bg-white border rounded-lg shadow-lg py-2 min-w-full z-10 hidden group-hover:block">
          {attachments.slice(1).map((attachment) => {
            return isUrlReady(attachment.id) ? (
              <a
                key={attachment.id}
                href={getUrlForAttachment(attachment.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 no-underline text-gray-900"
              >
                {AttachmentService.getFileIcon(attachment.mime_type)} {attachment.file_name}
              </a>
            ) : (
              <div
                key={attachment.id}
                className="block w-full text-left px-4 py-2 text-sm text-gray-400"
              >
                <div className="inline-block animate-spin rounded-full h-3 w-3 border-b-2 border-gray-400 mr-2"></div>
                読み込み中...
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

const VideoPlayerPage = () => {
  const { videoId } = useParams<{ videoId: string }>()
  const navigate = useNavigate()
  const [currentVideo, setCurrentVideo] = useState<VideoWithCategory | null>(null)
  const [relatedVideos, setRelatedVideos] = useState<VideoWithCategory[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [chatPrompt, setChatPrompt] = useState<ChatPromptWithVideo | null>(null)
  const [genericChatPrompt, setGenericChatPrompt] = useState<ChatPromptWithVideo | null>(null)
  const [currentUser, setCurrentUser] = useState<{ id: string } | null>(null)
  const [categoryName, setCategoryName] = useState<string>('')
  const [downloadUrls, setDownloadUrls] = useState<Record<string, string>>({})
  const chatInterfaceRef = useRef<ChatInterfaceRef>(null)
  const [videoHeight, setVideoHeight] = useState(0)
  const videoColumnRef = useCallback((el: HTMLDivElement | null) => {
    if (!el) return
    const observer = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect.width
      if (width > 0) setVideoHeight(width * 9 / 16)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // The layout switches at the `md` breakpoint. We track it in JS (instead of
  // rendering two separate trees) so the video iframe stays the same DOM node
  // across the switch and keeps playing.
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches
  )
  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 768px)')
    const handleChange = (event: MediaQueryListEvent) => setIsDesktop(event.matches)
    setIsDesktop(mediaQuery.matches)
    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  // Scroll to top when video changes
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    })
  }, [videoId])

  useEffect(() => {
    const fetchVideoData = async () => {
      if (!videoId) {
        setError('ビデオIDが指定されていません')
        setLoading(false)
        return
      }

      try {
        // Check if user is authenticated
        const user = await AuthService.getCurrentUser()
        if (!user) {
          // Redirect to landing page with login dialog and return URL
          const returnUrl = encodeURIComponent(`/video/${videoId}`)
          navigate(`/?login=true&returnUrl=${returnUrl}`)
          return
        }

        setCurrentUser({ id: user.id })

        // Save to watch history
        try {
          await VideoService.saveWatchHistory(user.id, videoId)
        } catch (watchErr) {
          console.warn('Could not save to watch history:', watchErr)
        }

        // Fetch current video
        const video = await VideoService.getVideo(videoId)
        if (!video) {
          setError('ビデオが見つかりませんでした')
          setLoading(false)
          return
        }
        setCurrentVideo(video)

        // Increment view count for this video
        try {
          await VideoService.incrementViewCount(video.id, user.id)
        } catch (viewError) {
          console.warn('Failed to track view:', viewError)
          // Don't throw error - view tracking shouldn't break video playback
        }

        // Get category name for display
        try {
          if (video.category_id) {
            const category = await CategoryService.getCategory(video.category_id)
            setCategoryName(category?.name || video.category)
          } else {
            setCategoryName(video.category) // Use legacy string value
          }
        } catch (err) {
          console.warn('Could not fetch category name:', err)
          setCategoryName(video.category) // Fallback to string value
        }

        // Fetch related videos from same category, excluding current video.
        // A school's own content has no related contents: the category match would
        // pull in unrelated Mirasso videos.
        if (!video.is_school_video) {
          const related = await VideoService.getVideos({
            category_id: video.category_id || undefined,
            category: video.category_id ? undefined : video.category, // Fallback to legacy filtering
            excludeId: videoId,
            limit: 10
          })
          setRelatedVideos(related)
        } else {
          setRelatedVideos([])
        }

        // Find this video's own chat prompt, plus the generic prompt used as a
        // fallback for videos that don't have their own (e.g. legacy videos).
        try {
          const [matchingPrompt, generic] = await Promise.all([
            ChatPromptService.getPromptByVideoId(video.id),
            ChatPromptService.getGenericChatPrompt().catch(() => null),
          ])
          setChatPrompt(matchingPrompt)
          setGenericChatPrompt(generic)
        } catch (promptErr) {
          console.warn('Could not fetch chat prompts:', promptErr)
          setChatPrompt(null)
        }

        // Preload attachment download URLs
        if (video.attachments && video.attachments.length > 0) {
          const urlPromises = video.attachments.map(async (attachment) => {
            try {
              const url = await AttachmentService.getDownloadUrl(attachment.id)
              return { id: attachment.id, url }
            } catch (error) {
              console.warn(`Failed to preload download URL for ${attachment.id}:`, error)
              return { id: attachment.id, url: null }
            }
          })

          const results = await Promise.all(urlPromises)
          const preloadedUrls: Record<string, string> = {}

          results.forEach(result => {
            if (result.url) {
              preloadedUrls[result.id] = result.url
            }
          })

          setDownloadUrls(preloadedUrls)
        }

        setLoading(false)
      } catch (err) {
        console.error('Error fetching video data:', err)
        setError('ビデオデータの取得に失敗しました')
        setLoading(false)
      }
    }

    fetchVideoData()
  }, [videoId])

  // Helper function to safely render tags
  const renderTags = (tags: unknown, maxTags: number = 3, sizeClass: string = "text-xs") => {
    // Type guard to ensure tags is a proper array of tag objects
    if (!Array.isArray(tags) || tags.length === 0) {
      return <span className={`${sizeClass} text-gray-400`}>タグなし</span>
    }

    // Filter and validate tag objects
    const validTags = tags
      .filter((tag): tag is { name: string; slug: string } | string =>
        (tag &&
        typeof tag === 'object' &&
        'name' in tag &&
        'slug' in tag &&
        typeof tag.name === 'string' &&
        typeof tag.slug === 'string') ||
        typeof tag === 'string'
      )
      .slice(0, maxTags)

    if (validTags.length === 0) {
      return <span className={`${sizeClass} text-gray-400`}>タグなし</span>
    }

    // Determine icon size and padding based on text size
    const isSmall = sizeClass === "text-xs"
    const iconClass = isSmall ? "w-3 h-3" : "w-4 h-4"
    const paddingClass = isSmall ? "px-1 py-0.5" : "px-2 py-1"

    return validTags.map((tag, index) => {
      const tagName = typeof tag === 'string' ? tag : tag.name
      const tagSlug = typeof tag === 'string' ? encodeURIComponent(tag) : encodeURIComponent(tag.slug)
      const key = typeof tag === 'string' ? `${tag}-${index}` : tag.slug

      return (
        <Link
          key={key}
          to={`/tags/${tagSlug}`}
          className={`flex items-center gap-1 ${sizeClass} hover:bg-gray-100 rounded ${paddingClass} transition-colors`}
          onClick={(e) => e.stopPropagation()}
        >
          <img src={hashtagIcon} alt="#" className={iconClass} />
          <span className="font-semibold text-gray-800">{tagName}</span>
        </Link>
      )
    })
  }

  // Helper function to get attachments by type
  const getAttachmentsByType = (type: 'course_material' | 'worksheet'): VideoAttachment[] => {
    return currentVideo?.attachments?.filter(attachment => attachment.attachment_type === type) || []
  }


  // Create context for ChatInterface component. Prefer the video's own chat
  // prompt; fall back to the generic prompt so every video shows a chat area.
  const effectiveChatPrompt = chatPrompt ?? genericChatPrompt
  const chatContext: VideoConversationContext | null = currentVideo && effectiveChatPrompt ? {
    type: 'video',
    video: currentVideo,
    chatPrompt: effectiveChatPrompt
  } : null

  // Handle reset chat functionality
  const handleResetChat = async () => {
    // Show confirmation dialog
    const confirmed = window.confirm('チャットの履歴をすべて削除してもよろしいですか？\nこの操作は取り消せません。')

    if (!confirmed) {
      return
    }

    // Call reset function via ref
    if (chatInterfaceRef.current) {
      await chatInterfaceRef.current.resetChat()
    }
  }

  // Component for Video Player section
  const VideoPlayerSection = () => {
    if (!currentVideo) return null

    return (
      <div>
        {/* Video Header */}
        <div className="bg-transparent text-pink-500 py-4 flex items-center gap-3">
          <img src={iconVideo} alt="" className="w-[45px] h-10" />
          <h1 className="font-bold text-white text-xl">{currentVideo.title}</h1>
        </div>
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
          {/* Video Player Area - Hidden for chat_only videos */}
          {currentVideo.video_type !== 'chat_only' && (
            <div className="relative bg-gray-900 aspect-video">
              {currentVideo.video_type === 'demo' ? (
                /* Demo Video Display - Just show the image */
                <VideoThumbnail thumbnailUrl={currentVideo.thumbnail_url} title={currentVideo.title} className="w-full h-full" resizeWidth={null} />
              ) : currentVideo.is_school_video && currentVideo.youtube_id ? (
                /* School-specific YouTube Video */
                <iframe
                  src={`https://www.youtube.com/embed/${currentVideo.youtube_id}`}
                  title={currentVideo.title}
                  className="w-full h-full"
                  allowFullScreen
                  allow="autoplay; fullscreen; picture-in-picture"
                />
              ) : (
                /* Regular Vimeo Video */
                <iframe
                  src={`https://player.vimeo.com/video/${currentVideo.vimeo_id}?badge=0&autopause=0&player_id=0&app_id=58479${currentVideo.vimeo_h ? `&h=${currentVideo.vimeo_h}` : ''}`}
                  title={currentVideo.title}
                  className="w-full h-full"
                  allowFullScreen
                  allow="autoplay; fullscreen; picture-in-picture"
                />
              )}
            </div>
          )}
        </div>
      </div>
    )
  }

  // Component for Video Metadata section (category, tags, description, attachments)
  const VideoMetadataSection = () => {
    if (!currentVideo) return null

    const courseMaterials = getAttachmentsByType('course_material')
    const worksheets = getAttachmentsByType('worksheet')

    return (
      <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
        <div className="p-6">
          {!currentVideo.is_school_video && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-gray-600">カテゴリ：</span>
                  <span className="text-sm font-medium text-blue-600 bg-blue-50 px-3 py-1 rounded-full">{categoryName}</span>
                </div>
                {currentVideo.video_type !== 'chat_only' && (
                  <div className="text-sm text-gray-500">
                    {currentVideo.view_count.toLocaleString()} 回視聴
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2 mb-3">
                {renderTags(currentVideo.tags, 10, "text-sm")}
              </div>
            </div>
          )}

          <div className="bg-gray-50 rounded-xl p-5">
            <p className="text-sm text-gray-600 mb-5 leading-relaxed">{currentVideo.description || '講義説明はありません'}</p>
            {(courseMaterials.length > 0 || worksheets.length > 0) && (
              <div className="flex flex-wrap justify-center gap-3">
                {/* Course Materials Download */}
                {courseMaterials.length > 0 && (
                  <DownloadSection
                    attachments={courseMaterials}
                    label={<>講義資料<br />ダウンロード</>}
                    className="bg-[#da4574] hover:bg-[#c93d68] text-white"
                    downloadUrls={downloadUrls}
                  />
                )}

                {/* Worksheets Download */}
                {worksheets.length > 0 && (
                  <DownloadSection
                    attachments={worksheets}
                    label={<>ワークシート<br />ダウンロード</>}
                    className="bg-orange-100 hover:bg-orange-200 text-orange-700"
                    downloadUrls={downloadUrls}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Component for AI Chat section
  const AIChatSection = () => {
    // For chat_only videos, line the bottom up with the standalone みらいワーク page (/chat):
    // its box is pt-4 (1rem) + h-[calc(100dvh-10rem)] min 360px max 600px, while here the chat
    // header (4.5rem) sits above the box, so subtract the 3.5rem difference from each bound.
    // For mobile or regular videos, use viewport-based height
    const chatHeight = currentVideo?.video_type === 'chat_only'
      ? "h-[calc(100dvh-13.5rem)] min-h-[304px] max-h-[544px]"
      : "h-[calc(100vh-20rem)]"

    // For chat_only videos, show the video title instead of "みらいワーク"
    const chatTitle = currentVideo?.video_type === 'chat_only' ? currentVideo.title : 'みらいワーク'

    // On desktop the chat sits beside the player and is matched to its height.
    // On mobile it is stacked below and uses the viewport-based height instead.
    const matchVideoHeight = isDesktop && videoHeight > 0 && currentVideo?.video_type !== 'chat_only'

    return (
      <div>
        {/* Chat Header */}
        <div className="bg-transparent text-pink-500 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={iconChat} alt="" className="w-[45px] h-10" />
            <h1 className="font-bold text-white text-xl">{chatTitle}</h1>
          </div>
          {chatContext && (
            <button
              onClick={handleResetChat}
              className="p-2 rounded-lg hover:bg-white/20 transition-colors"
              title="チャットをリセット"
            >
              <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                <line x1="10" y1="11" x2="10" y2="17" />
                <line x1="14" y1="11" x2="14" y2="17" />
              </svg>
            </button>
          )}
        </div>

        {/* Chat Interface */}
        {chatContext ? (
          <div style={matchVideoHeight ? { height: `${videoHeight}px` } : undefined}>
            <ChatInterface
              ref={chatInterfaceRef}
              context={chatContext}
              currentUser={currentUser}
              height={matchVideoHeight ? 'h-full' : chatHeight}
              showResetButton={false}
            />
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className={`${chatHeight} flex items-center justify-center p-4`}>
              <div className="text-center text-gray-500">
                <p>この動画にはみらいワークがありません</p>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  // Component for Related Videos section
  const RelatedVideosSection = () => (
    <div>
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-bold text-lg text-gray-900">関連コンテンツ</h2>
          <span className="text-sm text-gray-500">
            {relatedVideos.length} 件の動画
          </span>
        </div>
        {relatedVideos.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {relatedVideos.map((video) => VideoCard({ video }))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-400 text-4xl mb-4">🎬</div>
            <p className="text-gray-500">関連コンテンツはありません</p>
          </div>
        )}
      </div>
    </div>
  )

  const VideoCard = ({ video }: { video: VideoWithCategory }) => {
    return (
      <div key={video.id} className="bg-white rounded-lg overflow-hidden hover:shadow-md transition-shadow">
        {/* Clickable area for thumbnail and title */}
        <div
          className="cursor-pointer"
          onClick={() => navigate(`/video/${video.id}`)}
        >
          <div className="relative">
            <VideoThumbnail
              thumbnailUrl={video.thumbnail_url}
              title={video.title}
              className="w-full aspect-video"
              thumbnailTitle={video.thumbnail_title}
              schoolOriginal={video.is_school_video && video.video_type === 'chat_only'}
            />
          </div>
          <div className="p-3 pb-0">
            <h3 className="font-medium text-sm mb-1 line-clamp-2" style={{ color: '#da4574' }}>
              {video.title}
            </h3>
          </div>
        </div>

        {/* Tags - separate from clickable area */}
        {!video.is_school_video && (
          <div className="px-3 pb-3">
            <div className="flex flex-wrap gap-2">
              {renderTags(video.tags, 3, "text-xs")}
            </div>
          </div>
        )}
      </div>
    )
  }

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#da4574] mx-auto mb-4"></div>
          <p className="text-gray-600">動画を読み込んでいます...</p>
        </div>
      </div>
    )
  }

  if (error || !currentVideo) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-[#da4574] text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">エラーが発生しました</h2>
          <p className="text-gray-600">{error || '動画が見つかりませんでした'}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        {/*
          Mobile and desktop share a single DOM tree that CSS grid re-arranges at
          the `md` breakpoint. Rendering two trees (one `md:hidden`, one
          `hidden md:grid`) mounted the player twice: crossing the breakpoint only
          set `display: none` on the tree you were watching, which leaves its
          iframe playing audio while the newly shown one sits paused.
          Source order below is the mobile order; desktop positions are explicit.
        */}
        {currentVideo.video_type === 'chat_only' ? (
          /* Chat-only layout: chat takes the video position, metadata goes to the right sidebar */
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-y-0">
            {/* Title header - mobile only (desktop shows the title in the chat header) */}
            <div className="md:hidden">
              {VideoPlayerSection()}
            </div>

            {/* Right sidebar on desktop: thumbnail + metadata */}
            <div className="md:col-start-3 md:row-start-1">
              {/* Add spacing to align with ChatInterface (skip chat header height) */}
              <div className="hidden md:block h-[calc(1rem+2.5rem+1rem)]"></div>

              {/* Thumbnail for chat_only videos */}
              <div className="hidden md:block bg-white rounded-lg shadow-md overflow-hidden mb-6">
                <VideoThumbnail
                  thumbnailUrl={currentVideo.thumbnail_url}
                  title={currentVideo.title}
                  className="w-full aspect-video"
                  thumbnailTitle={currentVideo.thumbnail_title}
                  schoolOriginal={currentVideo.is_school_video && currentVideo.video_type === 'chat_only'}
                />
              </div>

              {VideoMetadataSection()}
            </div>

            <div className="md:col-span-2 md:col-start-1 md:row-start-1">
              {AIChatSection()}
            </div>

            {!currentVideo.is_school_video && (
              <div className="md:col-span-2 md:col-start-1 md:row-start-2">
                {RelatedVideosSection()}
              </div>
            )}
          </div>
        ) : (
          /* Regular/Demo layout: player + metadata + related on the left, chat on the right */
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-y-0">
            <div ref={videoColumnRef} className="md:col-span-2 md:col-start-1 md:row-start-1">
              {VideoPlayerSection()}
            </div>

            <div className="md:col-span-2 md:col-start-1 md:row-start-2">
              {VideoMetadataSection()}
            </div>

            {/* AI Chat - right sidebar on desktop, below the metadata on mobile */}
            <div className="md:col-start-3 md:row-start-1">
              {AIChatSection()}
            </div>

            {!currentVideo.is_school_video && (
              <div className="md:col-span-2 md:col-start-1 md:row-start-3">
                {RelatedVideosSection()}
              </div>
            )}
          </div>
        )}
    </div>
  )
}

export default VideoPlayerPage